import React from 'react';
import { Outlet } from 'react-router-dom';

import { canAccessApp, findNavByAppKey, isPublicHost } from '../config';
import { useAuth } from '../lib/auth';
import AccessDenied from '../pages/errors/AccessDenied.jsx';

/**
 * Route guard that complements ``RequireAuth``.
 *
 *   * ``RequireAuth`` only checks "is this request authenticated?".
 *   * ``RequirePermission`` answers "is this user allowed to OPEN this
 *     particular app?" using the ``permitted_apps`` list returned by
 *     ``/api/super_admin/me/`` and the rules in ``config.canAccessApp``.
 *
 * If the user can't open the app we render the shared ``AccessDenied``
 * page with a *specific* reason so the message is accurate:
 *
 *   * super-admin trying to open a tenant-only business app
 *     (Tasks / Complaints / Chat) →  ``not-for-superadmin``
 *     (matches the product brief copy: "This app is not available to
 *     super admins.").  Crucially we render this BEFORE the page would
 *     get a chance to fire its tenant API call — that's what stops the
 *     ``GET /api/task/tasks/summary/ → 404`` storm on ``tasky.do``.
 *
 *   * org user opening an admin-only shell (e.g. ``/business``) without
 *     ``is_admin`` →  ``not-for-admin``
 *     ("This app is not available to admins.").
 *
 *   * everyone else who fails the permission check →  ``unauthorized``
 *     ("You don't have permission to access this module. Please contact
 *      your admin.").
 *
 * Usage — both wrapper and Outlet styles are supported:
 *
 *     <RequirePermission appKey="task"><TasksPage /></RequirePermission>
 *
 *     <Route element={<RequirePermission appKey="task" />}>
 *       <Route path="/task" element={<TasksDashboard />} />
 *       …
 *     </Route>
 *
 * Security note: this is UX gating only.  The backend still enforces
 * the permission on every API call via the existing decorators,
 * ``TenantAwareJWTAuthentication`` and ``TenantUserBindingMiddleware``.
 * Hiding a route in React does not grant or revoke any access.
 *
 * Props
 * -----
 * ``appKey``       (string)   the canonical app key, matching one of the
 *                              entries in ``NAV`` / ``user.permitted_apps``
 *                              ("task", "complaints", "chat", "business",
 *                              "super-admin").  Accepts ``permission`` as
 *                              an alias so call-sites can read more
 *                              naturally — both refer to the same key.
 * ``permission``   (string)   alias for ``appKey``.
 */
export default function RequirePermission({
  appKey,
  permission,
  children,
}) {
  const { user, bootstrapping } = useAuth();
  const key = appKey || permission;

  if (bootstrapping) {
    return (
      <div className="ty-fullscreen-center">
        <div className="ty-spinner" aria-label="Loading" />
      </div>
    );
  }

  const nav = findNavByAppKey(key);
  if (!nav) {
    // Programmer error (typo in appKey) — fail closed with the generic
    // message so we never accidentally render the protected page.
    return <AccessDenied reason="unauthorized" />;
  }

  if (canAccessApp(nav, user)) {
    // Allowed — render the wrapped tree.  Support both wrapper-style
    // (<RequirePermission><X /></RequirePermission>) and Outlet-style
    // (<Route element={<RequirePermission appKey="x" />}>) usage.
    if (children) return children;
    return <Outlet />;
  }

  // ---- Denied — pick the most specific reason ---------------------------
  const isSuper = !!(user && (user.is_super_admin || user.is_superuser));
  const isAdmin = !!(user && user.is_admin);

  // 1. Super-admin opening a tenant-only business app.  This is the
  //    exact scenario in the brief: super-admin on tasky.do clicks
  //    "Tasks" → we must NOT call /api/task/...; instead show the
  //    "not available to super admins" page.
  if (isSuper && nav.tenantOnly && !nav.requireSuperAdmin) {
    return <AccessDenied reason="not-for-superadmin" />;
  }

  // 2. Org admin opening a non-admin app they otherwise lack the
  //    permission for.  Rare in practice (admins usually have all
  //    permissions) but keeps the messaging precise.
  if (isAdmin && nav.requireSuperAdmin) {
    return <AccessDenied reason="not-for-admin" />;
  }

  // 3. Everything else — generic "not authorised" message.
  return <AccessDenied reason="unauthorized" />;
}
